import AdminTextInput from 'components/atoms/adminTextInput';
import LongRectangleTextButton from 'components/atoms/longRectangleTextButton';
import RectangleImageButton from 'components/atoms/rectangleImageButton';
import AdminPanelUI from 'components/molecules/adminPanelUI';
import ContactDataList from 'components/molecules/contactDataList';
import {
  useAdminGroupsStore,
  useAdminGroupsStudentsStore,
  useLoaderStore,
} from 'contexts/StoreContext';
import {useTheme} from 'contexts/ThemeContext';
import {generateTempUid} from 'helpers/generateTempId';
import {observer} from 'mobx-react';
import {PersonalData} from 'models/Admin';
import {AdminPanelGroupsInteractionScreenProps} from 'navigations/GroupsFlow';
import {Screens} from 'navigations/Screens';
import React, {useState} from 'react';
import {useEffect} from 'react';
import {SafeAreaView, View, Text} from 'react-native';
import {
  CreateGroupData,
  StudentData,
  UpdateGroupData,
} from 'services/API/AdminApi/AdminApiModels';
import strings from 'translations/index.web';
import styleSheet from './styles';

const AdminPanelGroupsInteraction = observer(
  (props: AdminPanelGroupsInteractionScreenProps) => {
    const params = props.route.params;

    const theme = useTheme();
    const styles = styleSheet(theme);

    const loaderStore = useLoaderStore();
    const studentsStore = useAdminGroupsStudentsStore();
    const groupsStore = useAdminGroupsStore();

    const flatListData = studentsStore.students;

    const groupName = studentsStore.groupName;

    useEffect(() => {
      studentsStore.setGroup(params.id, params.title);
    }, []);

    const handleDelete = (deletable: PersonalData) => {
      studentsStore.deleteStudent(deletable);
    };

    const handleAdd = () => {
      studentsStore.addEmptyStudent();
    };

    const handleCreate = async () => {
      let newGroup: CreateGroupData = {name: groupName, students: flatListData};

      loaderStore.startLoading();
      await studentsStore.groupsStore.createGroup(newGroup);
      await studentsStore.groupsStore.getGroups();
      loaderStore.stopLoading();

      props.navigation.pop();
    };

    const handleEdit = async () => {
      let mappedGroup = studentsStore.mapGroupToRequestFormat();

      let updatedGroup: UpdateGroupData = {
        name: groupName,
        students: mappedGroup,
        id: params.id,
      };

      loaderStore.startLoading();
      await studentsStore.groupsStore.updateGroups(updatedGroup);
      await groupsStore.getGroups();
      loaderStore.stopLoading();

      props.navigation.pop();
    };

    const handleCancel = async () => {
      loaderStore.startLoading();
      await groupsStore.getGroups();
      loaderStore.stopLoading();
      props.navigation.pop();
    };

    const onFieldChanged = (
      text: string,
      arrIndex: number,
      fieldName: string,
    ) => {
      studentsStore.setField(text, arrIndex, fieldName);
    };

    const ListView = (
      <View style={styles.listContainer}>
        <ContactDataList
          flatListData={flatListData}
          onPressDelete={(deletable: PersonalData) => handleDelete(deletable)}
          onFieldEdit={(text, index, fieldName) =>
            onFieldChanged(text, index, fieldName)
          }
        />
        <View style={styles.buttonContainer}>
          <RectangleImageButton
            image={theme.images.addIcon}
            backgroundColor={theme.colors.white}
            onPress={() => handleAdd()}
          />
        </View>
      </View>
    );

    const GroupHeader = (
      <SafeAreaView style={styles.groupHeaderContainer}>
        <Text style={styles.groupHeaderTextStyle}>
          {strings.admin.groupInfo}
        </Text>
        <AdminTextInput
          title={strings.admin.groupName}
          placeholder={strings.admin.groupName}
          isGeneral={false}
          value={groupName}
          onChangeText={text => {
            studentsStore.setGroupName(text);
          }}
        />
      </SafeAreaView>
    );

    const ButtonView = (
      <View style={styles.bottomViewButtonContainer}>
        <LongRectangleTextButton
          onPress={() => {
            handleCancel();
          }}
          title={strings.admin.cancel}
          titleColor={styles.cancelButtonTextColor}
          buttonStyle={styles.cancelButtonStyle}
        />
        <LongRectangleTextButton
          onPress={() =>
            params.title === strings.admin.createGroup
              ? handleCreate()
              : handleEdit()
          }
          title={params.title}
          titleColor={styles.secondButtonTextColor}
          buttonStyle={styles.secondButtonStyle}
        />
      </View>
    );

    return (
      <AdminPanelUI
        title={params.title}
        activeName={Screens.AdminPanelGroupsScreen.name}
        isGroupsInteraction={true}>
        <View>
          {GroupHeader}
          {ListView}
          {ButtonView}
        </View>
      </AdminPanelUI>
    );
  },
);

export default AdminPanelGroupsInteraction;
